import { useNavigate, useParams } from "@tanstack/react-router";
import { useGeofenceViolationsQuery } from "@/api/v0/geofence-violations";
import { useGeofenceQueryV1 } from "@/api/v1/geofence-api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, ChevronUp, Plane } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState, useMemo } from "react";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import { Badge } from "@/components/ui/badge";

interface FlightSession {
  droneId: string;
  startTime: Date;
  endTime: Date;
  violationCount: number;
}

export function GeofenceViolationsRoute() {
  const navigate = useNavigate();
  const { geofenceId } = useParams({ from: "/geofences/$geofenceId/violations" });
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  const { data: geofence, isLoading: isLoadingGeofence } = useGeofenceQueryV1(geofenceId);
  const { data: violations, isLoading: isLoadingViolations } = useGeofenceViolationsQuery(
    geofenceId,
    {}
  );

  // Group violations into flight sessions
  const flightSessions = useMemo(() => {
    if (!violations?.drones) return [];

    const sessions: FlightSession[] = [];
    const TIME_GAP_THRESHOLD = 10 * 60 * 1000; // 10 minutes in milliseconds

    violations.drones.forEach((drone) => {
      // If there's only one violation or the duration is less than our threshold,
      // treat it as a single session
      const startTime = new Date(drone.firstViolation);
      const endTime = new Date(drone.lastViolation);
      const duration = endTime.getTime() - startTime.getTime();

      if (duration <= TIME_GAP_THRESHOLD) {
        sessions.push({
          droneId: drone.droneIdentifier,
          startTime,
          endTime,
          violationCount: drone.violationCount
        });
        return;
      }

      // For longer periods, we need to analyze the violation pattern
      // Calculate violations per second to detect continuous periods
      const violationsPerSecond = drone.violationCount / (duration / 1000);
      
      // If we're getting roughly 1 violation per second (with some tolerance),
      // it's likely a continuous flight
      if (violationsPerSecond >= 0.8 && violationsPerSecond <= 1.2) {
        sessions.push({
          droneId: drone.droneIdentifier,
          startTime,
          endTime,
          violationCount: drone.violationCount
        });
        return;
      }

      // If we have gaps in violations, split into multiple sessions
      // For now, we'll use a simple time-based split as we don't have the individual violation timestamps
      const estimatedGaps = Math.floor(duration / TIME_GAP_THRESHOLD) - 1;
      const violationsPerSession = Math.ceil(drone.violationCount / (estimatedGaps + 1));
      
      let currentTime = startTime.getTime();
      let remainingViolations = drone.violationCount;

      while (currentTime < endTime.getTime() && remainingViolations > 0) {
        const sessionEndTime = new Date(Math.min(
          currentTime + TIME_GAP_THRESHOLD,
          endTime.getTime()
        ));

        const sessionViolations = Math.min(
          violationsPerSession,
          remainingViolations
        );

        sessions.push({
          droneId: drone.droneIdentifier,
          startTime: new Date(currentTime),
          endTime: sessionEndTime,
          violationCount: sessionViolations
        });

        remainingViolations -= sessionViolations;
        currentTime = sessionEndTime.getTime() + TIME_GAP_THRESHOLD;
      }
    });

    return sessions.sort((a, b) => b.endTime.getTime() - a.endTime.getTime());
  }, [violations]);

  // Group sessions by drone ID for better organization
  const sessionsByDrone = useMemo(() => {
    const grouped = new Map<string, FlightSession[]>();
    flightSessions.forEach(session => {
      if (!grouped.has(session.droneId)) {
        grouped.set(session.droneId, []);
      }
      grouped.get(session.droneId)!.push(session);
    });
    return grouped;
  }, [flightSessions]);

  const toggleSession = (droneId: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev);
      if (next.has(droneId)) {
        next.delete(droneId);
      } else {
        next.add(droneId);
      }
      return next;
    });
  };

  if (isLoadingGeofence || isLoadingViolations) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!geofence) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center text-destructive">
          Geofence not found.
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8 space-y-6">
      <BreadcrumbNav
        items={[
          { label: "Home", href: "/" },
          { label: "Geofences", href: "/geofences" },
          { label: geofence?.name ?? "Geofence" },
        ]}
        className="mb-6"
      />
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate({ to: "/geofences" })}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Geofences
        </Button>
      </div>

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">{geofence.name} - Violations</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Violation History</CardTitle>
          <CardDescription>
            Violations are grouped into estimated flight sessions based on timing patterns.
            Click on a drone to see its individual flight sessions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sessionsByDrone.size > 0 ? (
            <div className="space-y-4">
              {Array.from(sessionsByDrone.entries()).map(([droneId, sessions]) => {
                const totalViolations = sessions.reduce((sum, s) => sum + s.violationCount, 0);
                const isExpanded = expandedSessions.has(droneId);
                
                return (
                  <div key={droneId} className="border rounded-lg p-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleSession(droneId)}
                    >
                      <div className="flex items-center gap-2">
                        <Plane className="h-4 w-4" />
                        <div>
                          <h3 className="font-medium">{droneId}</h3>
                          <p className="text-sm text-muted-foreground">
                            {sessions.length} flight{sessions.length !== 1 ? 's' : ''} • {totalViolations} total violations
                          </p>
                        </div>
                      </div>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>

                    {isExpanded && (
                      <Table className="mt-4">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Start Time</TableHead>
                            <TableHead>Duration</TableHead>
                            <TableHead>Violations</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sessions.map((session, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {new Date(session.startTime).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                {Math.round((session.endTime.getTime() - session.startTime.getTime()) / 60000)} minutes
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">
                                  {session.violationCount}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-muted-foreground mt-2">
                No violations found in the selected date range.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
} 